/* Beeloma product page behaviour, shared by the beeloma-* sections.
   - bl-slider: scroll-snap gallery / review carousel with thumbs or dots
   - bl-buy-box: bundle + subscription picker and add to cart. The buy box section is the
     primary; the final CTA section mirrors its picker and shares the same selection.
   - bl-reviews: concern filter, optional sort select + "read more"
   Add to cart mirrors Dawn's assets/product-form.js so the theme's cart notification / drawer opens,
   or (buy box "After add to cart" = checkout) adds the pack and goes straight to checkout. */
(() => {
  if (window.BeelomaPdp) return;

  const store = {
    data: null,
    ready: false,
    bundle: 0,
    sub: false,
    listeners: new Set(),
    set(patch) {
      Object.assign(this, patch);
      this.listeners.forEach((fn) => fn());
    },
  };
  window.BeelomaPdp = store;

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => (reduceMotion() ? 'auto' : 'smooth');

  function setText(root, selector, text) {
    const el = root.querySelector(selector);
    if (el && text != null) el.textContent = text;
  }

  function addBusinessDays(days) {
    const d = new Date();
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const w = d.getDay();
      if (w !== 0 && w !== 6) added++;
    }
    return d.toLocaleDateString(document.documentElement.lang || 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function scrollToEl(el, offset) {
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: behavior() });
  }

  // Anchor jumps (#bl-buy, #bl-reviews, contact tabs), the collapsed contact form and UGC video play buttons.
  document.addEventListener('click', (e) => {
    const formLink = e.target.closest('a[href="#bl-contact-form"]');
    if (formLink) {
      const card = document.getElementById('bl-contact-form');
      if (!card) return;
      e.preventDefault();
      card.classList.add('is-open');
      requestAnimationFrame(() => {
        scrollToEl(card, 80);
        setTimeout(() => {
          const field = card.querySelector('input:not([type="hidden"]), textarea');
          if (field) field.focus({ preventScroll: true });
        }, 500);
      });
      return;
    }
    const jump = e.target.closest('a[data-bl-jump]');
    if (jump) {
      const target = document.getElementById(jump.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      scrollToEl(target, Number(jump.dataset.blOffset) || 8);
      return;
    }
    const play = e.target.closest('[data-bl-play]');
    if (play) {
      const card = play.closest('.bl-ugc');
      const video = card && card.querySelector('video');
      if (!video) return;
      e.preventDefault();
      card.classList.add('is-playing');
      video.controls = true;
      video.muted = false;
      video.play();
    }
  });

  // Contact / newsletter validation and the order tracking box.
  const EMAIL = /^\S+@\S+\.\S+$/;
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-bl-validate]')) {
      let first = null;
      form.querySelectorAll('[data-bl-req]').forEach((field) => {
        const value = field.value.trim();
        const bad = field.dataset.blReq === 'email' ? !EMAIL.test(value) : !value;
        const err = form.querySelector(`[data-bl-err-for="${field.id}"]`);
        if (err) err.hidden = !bad;
        field.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (bad && !first) first = field;
      });
      if (first) {
        e.preventDefault();
        first.focus();
      }
      return;
    }
    if (form.matches('[data-bl-track]')) {
      e.preventDefault();
      const code = form.querySelector('input').value.trim();
      const msg = form.parentElement.querySelector('[data-bl-track-msg]');
      if (!code) {
        if (msg) {
          msg.textContent = form.dataset.emptyMsg;
          msg.hidden = false;
        }
        return;
      }
      const url = form.dataset.url.replace('[code]', encodeURIComponent(code));
      if (msg) {
        msg.textContent = form.dataset.lookingMsg.replace('[code]', code);
        msg.hidden = false;
      }
      if (/^https?:/i.test(url)) window.open(url, '_blank', 'noopener');
      else window.location.href = url;
    }
  });

  document.addEventListener('input', (e) => {
    const field = e.target.closest('[data-bl-req]');
    if (!field || !field.id) return;
    const err = document.querySelector(`[data-bl-err-for="${field.id}"]`);
    if (err) err.hidden = true;
    field.removeAttribute('aria-invalid');
  });

  if (!customElements.get('bl-tabs')) {
    customElements.define(
      'bl-tabs',
      class BlTabs extends HTMLElement {
        // Active tab = the last target whose top has passed 40% of the viewport (computed from scroll
        // position, so jumps straight to the top or bottom can't leave a stale tab highlighted).
        connectedCallback() {
          this.links = [...this.querySelectorAll('a[href^="#"]')];
          this.targets = this.links
            .map((a) => document.getElementById(a.getAttribute('href').slice(1)))
            .filter(Boolean);
          if (!this.targets.length) return;
          this.onScroll = () => {
            cancelAnimationFrame(this.raf);
            this.raf = requestAnimationFrame(() => this.update());
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.update();
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        }

        update() {
          const line = window.innerHeight * 0.4;
          let active = this.targets[0];
          this.targets.forEach((t) => {
            if (t.getBoundingClientRect().top <= line) active = t;
          });
          if (active.id !== this.current) {
            this.current = active.id;
            this.mark(active.id);
          }
        }

        mark(id) {
          this.links.forEach((a) => {
            const on = a.getAttribute('href') === `#${id}`;
            a.setAttribute('aria-current', on ? 'true' : 'false');
            const strip = a.parentElement;
            if (on && strip.scrollWidth > strip.clientWidth) {
              strip.scrollTo({ left: a.offsetLeft - 24, behavior: behavior() });
            }
          });
        }
      }
    );
  }

  if (!customElements.get('bl-slider')) {
    customElements.define(
      'bl-slider',
      class BlSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-bl-track]');
          if (!this.track) return;
          this.controls = [...this.querySelectorAll('[data-bl-go]')];
          this.count = this.querySelector('[data-bl-count]');
          this.total = this.track.children.length;
          this.index = 0;
          this.onScroll = () => {
            cancelAnimationFrame(this.raf);
            this.raf = requestAnimationFrame(() =>
              this.mark(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth)))
            );
          };
          this.onControl = (e) => {
            const btn = e.target.closest('[data-bl-go]');
            if (btn) this.go(Number(btn.dataset.blGo));
          };
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('click', this.onControl);
        }

        disconnectedCallback() {
          if (!this.track) return;
          this.track.removeEventListener('scroll', this.onScroll);
          this.removeEventListener('click', this.onControl);
        }

        go(i) {
          this.track.scrollTo({ left: i * this.track.clientWidth, behavior: behavior() });
          this.mark(i);
        }

        mark(i) {
          if (i === this.index) return;
          this.index = i;
          this.controls.forEach((c, n) => c.setAttribute('aria-current', n === i ? 'true' : 'false'));
          if (this.count) this.count.textContent = `${i + 1} / ${this.total}`;
          const active = this.controls[i];
          const strip = active && active.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            strip.scrollTo({ left: active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2, behavior: behavior() });
          }
        }
      }
    );
  }

  if (!customElements.get('bl-carousel')) {
    customElements.define(
      'bl-carousel',
      class BlCarousel extends HTMLElement {
        // Previous/next arrows for a scroll-snap row: one card per step, dimmed at either end,
        // hidden when every card already fits.
        connectedCallback() {
          this.track = this.querySelector('[data-bl-track]');
          this.nav = this.querySelector('[data-bl-nav]');
          if (!this.track) return;
          this.onScroll = () => {
            cancelAnimationFrame(this.raf);
            this.raf = requestAnimationFrame(() => this.sync());
          };
          this.onControl = (e) => {
            const btn = e.target.closest('[data-bl-step]');
            if (btn) this.step(Number(btn.dataset.blStep));
          };
          // Theme editor: bring the selected Review block into view.
          this.onSelect = (e) => {
            if (this.track.contains(e.target)) {
              this.track.scrollTo({ left: e.target.offsetLeft, behavior: behavior() });
            }
          };
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('click', this.onControl);
          this.addEventListener('shopify:block:select', this.onSelect);
          if (window.ResizeObserver) {
            this.resize = new ResizeObserver(this.onScroll);
            this.resize.observe(this.track);
          }
          this.sync();
        }

        disconnectedCallback() {
          if (!this.track) return;
          this.track.removeEventListener('scroll', this.onScroll);
          this.removeEventListener('click', this.onControl);
          this.removeEventListener('shopify:block:select', this.onSelect);
          if (this.resize) this.resize.disconnect();
        }

        step(dir) {
          const card = this.track.firstElementChild;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const width = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
          this.track.scrollBy({ left: dir * width, behavior: behavior() });
        }

        sync() {
          if (!this.nav) return;
          const max = this.track.scrollWidth - this.track.clientWidth;
          const left = this.track.scrollLeft;
          this.nav.hidden = max <= 2;
          this.nav.querySelector('[data-bl-step="-1"]').disabled = left <= 2;
          this.nav.querySelector('[data-bl-step="1"]').disabled = left >= max - 2;
        }
      }
    );
  }

  if (!customElements.get('bl-buy-box')) {
    customElements.define(
      'bl-buy-box',
      class BlBuyBox extends HTMLElement {
        connectedCallback() {
          this.isPrimary = this.hasAttribute('data-primary');
          if (this.isPrimary) {
            this.load();
          } else {
            this.mirror();
          }

          this.render = this.render.bind(this);
          this.onClick = this.onClick.bind(this);
          this.onKey = this.onKey.bind(this);
          store.listeners.add(this.render);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKey);
          this.render();
        }

        disconnectedCallback() {
          store.listeners.delete(this.render);
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKey);
          if (this.io) this.io.disconnect();
        }

        load() {
          const json = this.querySelector('[data-bl-data]');
          if (!json) return;
          try {
            store.data = JSON.parse(json.textContent);
          } catch (e) {
            return;
          }
          const d = store.data;
          const fresh = !store.ready || (window.Shopify && window.Shopify.designMode);
          if (fresh || store.bundle >= d.bundles.length) store.bundle = d.selected;
          if (fresh) store.sub = d.subDefault;
          if (!d.hasSub) store.sub = false;
          store.ready = true;

          document.querySelectorAll('[data-bl-lowest]').forEach((el) => {
            if (d.lowestPerDay) el.textContent = d.lowestPerDay;
          });
          const date = this.querySelector('[data-bl-date]');
          if (date) date.textContent = addBusinessDays(Number(date.dataset.days) || 3);
          document.querySelectorAll('bl-buy-box[data-mirror]').forEach((m) => {
            if (typeof m.mirror === 'function') m.mirror();
          });
          this.watchSticky();
        }

        mirror() {
          const slot = this.querySelector('[data-bl-mirror]');
          const primary = document.querySelector('bl-buy-box[data-primary]');
          const picker = primary && primary.querySelector('[data-bl-picker]');
          if (!slot || !picker) return;
          const copy = picker.cloneNode(true);
          copy.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
          slot.replaceChildren(copy);
          // Optional copy of the buy box's subscription / one-time note under this button.
          const noteSlot = this.querySelector('[data-bl-mirror-notes]');
          const notes = primary.querySelector('[data-bl-notes]');
          if (noteSlot && notes) noteSlot.replaceChildren(notes.cloneNode(true));
          if (this.render) this.render();
        }

        watchSticky() {
          const bar = this.querySelector('[data-bl-sticky]');
          const cta = this.querySelector('[data-bl-atc-main]');
          if (!bar || !cta || !('IntersectionObserver' in window)) return;
          if (this.io) this.io.disconnect();
          // The root extends far below the viewport, so "not intersecting" means the button has
          // scrolled up past the top. A plain observer misses jumps straight from below to above.
          this.io = new IntersectionObserver(
            ([entry]) => {
              const show = !entry.isIntersecting;
              bar.classList.toggle('is-visible', show);
              bar.inert = !show;
            },
            { rootMargin: '0px 0px 100000px 0px' }
          );
          this.io.observe(cta);
        }

        priceFor(b) {
          return store.sub && b.sub ? b.sub : b.one;
        }

        render() {
          const d = store.data;
          const b = d && d.bundles[store.bundle];
          if (!b) return;
          const p = this.priceFor(b);

          this.querySelectorAll('[data-bl-bundle]').forEach((el) => {
            const n = Number(el.dataset.blBundle);
            const bb = d.bundles[n];
            if (!bb) return;
            const pp = this.priceFor(bb);
            const on = n === store.bundle;
            el.setAttribute('aria-checked', on ? 'true' : 'false');
            el.tabIndex = on ? 0 : -1;
            setText(el, '[data-bl-total]', pp.total);
            setText(el, '[data-bl-perday]', pp.perDay);
            const cmp = el.querySelector('[data-bl-compare]');
            if (cmp) {
              cmp.textContent = pp.compare;
              cmp.hidden = !pp.showCompare;
            }
          });

          this.querySelectorAll('[data-bl-price]').forEach((el) => (el.textContent = p.total));
          this.querySelectorAll('[data-bl-price-compare]').forEach((el) => {
            el.textContent = p.compare;
            el.hidden = !p.showCompare;
          });
          this.querySelectorAll('[data-bl-save]').forEach((el) => {
            el.textContent = (d.labels.save || '').replace('[percent]', p.save);
            el.hidden = !(p.save > 0) || !d.labels.save;
          });

          this.querySelectorAll('[data-bl-sub]').forEach((el) => el.setAttribute('aria-checked', store.sub ? 'true' : 'false'));
          this.querySelectorAll('[data-bl-if-sub]').forEach((el) => (el.hidden = !store.sub));
          this.querySelectorAll('[data-bl-if-once]').forEach((el) => (el.hidden = store.sub));

          this.querySelectorAll('[data-bl-atc]').forEach((btn) => {
            btn.disabled = !b.available;
            setText(btn, '[data-bl-atc-label]', b.available ? d.labels.add : d.labels.soldOut);
            const price = btn.querySelector('[data-bl-atc-price]');
            if (price) {
              price.textContent = ` · ${p.total}`;
              price.hidden = !b.available;
            }
          });

          const line = this.querySelector('[data-bl-sticky-line]');
          if (line) {
            let text = d.labels.stickyLine.replace('[bundle]', b.short).replace('[per_day]', p.perDay).replace('[total]', p.total);
            if (store.sub && b.sub && d.labels.stickySub) text += ` ${d.labels.stickySub.replace('[percent]', b.pct)}`;
            line.textContent = text;
          }
        }

        onClick(e) {
          const bundle = e.target.closest('[data-bl-bundle]');
          if (bundle) {
            store.set({ bundle: Number(bundle.dataset.blBundle) });
            return;
          }
          if (e.target.closest('[data-bl-sub]')) {
            store.set({ sub: !store.sub });
            return;
          }
          const atc = e.target.closest('[data-bl-atc]');
          if (atc) {
            e.preventDefault();
            this.addToCart(atc);
          }
        }

        onKey(e) {
          const current = e.target.closest('[data-bl-bundle]');
          if (!current || !store.data) return;
          const n = store.data.bundles.length;
          let i = store.bundle;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') i = (i + 1) % n;
          else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') i = (i - 1 + n) % n;
          else return;
          e.preventDefault();
          store.set({ bundle: i });
          const next = current.parentElement.querySelector(`[data-bl-bundle="${i}"]`);
          if (next) next.focus();
        }

        showError(message) {
          this.querySelectorAll('[data-bl-error]').forEach((el) => {
            el.textContent = message || '';
            el.hidden = !message;
          });
        }

        addToCart(btn) {
          const d = store.data;
          const b = d && d.bundles[store.bundle];
          if (!b || !b.available || btn.getAttribute('aria-busy') === 'true') return;
          this.showError('');
          if (d.afterAdd === 'checkout') {
            this.buyNow(btn, b);
            return;
          }

          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const body = new FormData();
          body.append('id', b.id);
          body.append('quantity', b.qty);
          if (store.sub && b.sub && b.plan) body.append('selling_plan', b.plan);
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.append('sections', cart.getSectionsToRender().map((section) => section.id));
            body.append('sections_url', window.location.pathname);
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
          }

          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : { method: 'POST', headers: { Accept: 'application/javascript' } };
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = body;

          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const addUrl = (window.routes && window.routes.cart_add_url) || `${root}cart/add`;
          const canPublish = typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

          btn.setAttribute('aria-busy', 'true');
          fetch(addUrl, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                if (canPublish) {
                  publish(PUB_SUB_EVENTS.cartError, {
                    source: 'product-form',
                    productVariantId: b.id,
                    errors: response.errors || response.description,
                    message: response.message,
                  });
                }
                this.showError(response.description || response.message || d.labels.error);
                return;
              }
              if (!cart) {
                window.location = (window.routes && window.routes.cart_url) || `${root}cart`;
                return;
              }
              if (canPublish) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: b.id, cartData: response });
              }
              cart.renderContents(response);
              cart.classList.remove('is-empty');
            })
            .catch(() => this.showError(d.labels.error))
            .finally(() => btn.removeAttribute('aria-busy'));
        }

        // Skip the cart: optionally empty it, add the chosen pack (with its selling plan), then open checkout.
        // Cart permalinks can't carry selling plans, so this goes through the Ajax cart API.
        buyNow(btn, b) {
          const d = store.data;
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const post = (path, body) =>
            fetch(`${root}${path}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body || {}),
            });
          const item = { id: b.id, quantity: b.qty };
          if (store.sub && b.sub && b.plan) item.selling_plan = b.plan;
          const fail = (message) => {
            this.showError(message || d.labels.error);
            btn.removeAttribute('aria-busy');
          };

          btn.setAttribute('aria-busy', 'true');
          (d.replaceCart ? post('cart/clear.js') : Promise.resolve())
            .then(() => post('cart/add.js', { items: [item] }))
            .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
            .then(({ ok, json }) => {
              if (!ok || json.status) {
                fail(json.description || json.message);
                return;
              }
              window.location.href = `${root}checkout`;
            })
            .catch(() => fail());
        }
      }
    );
  }

  // Coming back from checkout restores the page from the back/forward cache with the button still busy.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('[data-bl-atc][aria-busy]').forEach((el) => el.removeAttribute('aria-busy'));
  });

  if (!customElements.get('bl-reviews')) {
    customElements.define(
      'bl-reviews',
      class BlReviews extends HTMLElement {
        connectedCallback() {
          this.items = [...this.querySelectorAll('[data-bl-review]')];
          this.chips = [...this.querySelectorAll('[data-bl-chip]')];
          this.more = this.querySelector('[data-bl-more]');
          this.sort = this.querySelector('[data-bl-sort]');
          this.showing = this.querySelector('[data-bl-showing]');
          this.limit = Number(this.dataset.limit) || 4;
          this.filter = 'all';
          this.expanded = false;
          this.onChange = (e) => {
            if (e.target !== this.sort) return;
            this.reorder();
            this.render();
          };
          if (this.sort) {
            this.addEventListener('change', this.onChange);
            this.reorder();
          }
          this.onClick = (e) => {
            const chip = e.target.closest('[data-bl-chip]');
            if (chip) {
              this.filter = chip.dataset.blChip;
              this.render();
              return;
            }
            if (e.target.closest('[data-bl-more]')) {
              this.expanded = true;
              this.render();
            }
          };
          this.addEventListener('click', this.onClick);
          this.render();
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('change', this.onChange);
        }

        // Most recent (data-date), Highest rated (data-rating) or With photos first. Ties keep block order.
        reorder() {
          if (!this.items.length) return;
          const by = this.sort.value;
          const key = (el) => {
            if (by === 'rating') return Number(el.dataset.rating) || 0;
            if (by === 'photos') return el.dataset.photo ? 1 : 0;
            return Date.parse(el.dataset.date || '') || 0;
          };
          this.items = [...this.items].sort((a, b) => key(b) - key(a));
          const before = this.more && this.more.parentElement === this.items[0].parentElement ? this.more : null;
          this.items.forEach((el) => el.parentElement.insertBefore(el, before));
        }

        render() {
          let matches = 0;
          this.items.forEach((el) => {
            const ok = this.filter === 'all' || el.dataset.concern === this.filter;
            if (ok) matches++;
            el.hidden = !(ok && (this.expanded || matches <= this.limit));
          });
          this.chips.forEach((c) => c.setAttribute('aria-pressed', c.dataset.blChip === this.filter ? 'true' : 'false'));
          if (this.showing) {
            const chip = this.chips.find((c) => c.dataset.blChip === this.filter);
            if (chip) this.showing.textContent = chip.textContent.trim().toLowerCase();
          }
          if (this.more) this.more.hidden = this.expanded || matches <= this.limit;
        }
      }
    );
  }
})();
